import { supabase } from '../lib/supabase';

// Helper: Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Helper: Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// Add participant
export const addParticipant = async (participantData) => {
  try {
    const snakeData = toSnakeCase(participantData);
    const { data, error } = await supabase?.from('participants')?.insert(snakeData)?.select()?.single();

    if (error) throw error;
    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error adding participant:', error);
    return { data: null, error };
  }
};

// Get all participants
export const getAllParticipants = async () => {
  try {
    const { data, error } = await supabase?.from('participants')?.select('*')?.order('participated_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error fetching participants:', error);
    return { data: [], error };
  }
};

// Get participants by content type
export const getParticipantsByType = async (contentType) => {
  try {
    const { data, error } = await supabase?.from('participants')?.select('*')?.eq('content_type', contentType)?.order('participated_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error fetching participants by type:', error);
    return { data: [], error };
  }
};

// Add giveaway participant
export const addGiveawayParticipant = async (giveawayId, instagramUsername) => {
  try {
    const { data, error } = await supabase?.from('giveaway_participants')?.insert({
        giveaway_id: giveawayId,
        instagram_username: instagramUsername
      })?.select()?.single();

    if (error) {
      // Check if already participated
      if (error?.code === '23505') {
        return { data: null, error: { message: 'Already participated' } };
      }
      throw error;
    }
    return { data: toCamelCase(data), error: null };
  } catch (error) {
    console.error('Error adding giveaway participant:', error);
    return { data: null, error };
  }
};

// Get giveaway participants
export const getGiveawayParticipants = async (giveawayId) => {
  try {
    const { data, error } = await supabase?.from('giveaway_participants')?.select('*')?.eq('giveaway_id', giveawayId)?.order('participated_at', { ascending: false });

    if (error) throw error;
    return { data: data?.map(toCamelCase), error: null };
  } catch (error) {
    console.error('Error fetching giveaway participants:', error);
    return { data: [], error };
  }
};

// Get poll results for today's poll (counts votes per option)
export const getPollResults = async (pollDate) => {
  try {
    const targetDate = pollDate || new Date()?.toISOString()?.split('T')?.[0];
    const { data, error } = await supabase
      ?.from('participants')
      ?.select('interaction_data')
      ?.eq('content_type', 'poll')
      ?.eq('date', targetDate);

    if (error) throw error;

    const voteCounts = {};
    let totalVotes = 0;

    data?.forEach((row) => {
      const option = row?.interaction_data?.option;
      if (option) {
        voteCounts[option] = (voteCounts?.[option] || 0) + 1;
        totalVotes++;
      }
    });

    return { data: { voteCounts, totalVotes }, error: null };
  } catch (error) {
    console.error('Error fetching poll results:', error);
    return { data: { voteCounts: {}, totalVotes: 0 }, error };
  }
};

// Clear all participants
export const clearAllParticipants = async () => {
  try {
    const { error } = await supabase?.from('participants')?.delete()?.gte('participated_at', '2000-01-01');
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Error clearing participants:', error);
    return { error };
  }
};